import React, {Component} from 'react';
import classnames from 'classnames';
import CheckboxField from '../lib/mdb/checkbox-field.js';
import TextField from '../lib/mdb/text-field';
import SelectField from '../lib/mdb/select-field';
import {mapMessages} from '../lib/utils';
import {validators} from '../lib/ext-refs';
import _set from 'lodash/set';
import _get from 'lodash/get';
import _unset from 'lodash/unset';
import {
    isWalletPM,
    updateTotals
} from '../lib/payment-utils';

import {
    FieldsConfig,
    getWalletIndex,
    StaticAchAgreement
} from "../lib/payment-method-util";

import PaymentMethodCashFields from "./payment-method/payment-method-cash-fields";
import PaymentMethodCardPresentFields from "./payment-method/payment-method-card-present-fields";
import PaymentMethodWalletFields from "./payment-method/payment-method-wallet-fields";
import PaymentMethodLastUsedFields from "./payment-method/payment-method-last-used-fields";
import PaymentMethodCCFields from "./payment-method/payment-method-cc-fields";
import PaymentMethodDCFields from "./payment-method/payment-method-dc-fields";
import PaymentMethodDDFields from "./payment-method/payment-method-dd-fields";
import PaymentMethodPDFields from "./payment-method/payment-method-pd-fields";


/**
 * Renders the payment method section.
 *
 * Expected parameters:
 * paymentMethod - current payment method
 * active - true if there are valid accounts
 * lastUsedPm - last used saved payment method
 * state
 * appComponents - customized components
 */
class PaymentMethodInfo extends Component {
    constructor(props) {
        super(props);
        this.onCardNumberChanged = this.onCardNumberChanged.bind(this);
        this.onPmCategoryChange = this.onPmCategoryChange.bind(this);
        this.onLanguageChange = this.onLanguageChange.bind(this);
        
        const {state} = props;
        const {data, config} = state;
        const {useCardPresentDedicatedFlow} = config;

        this.disableUpdateTotals = props.disableUpdateTotals;
        this.appComponents = props.appComponents;
        const {PaymentMethodCardPresentFields} = this.appComponents;
        if (useCardPresentDedicatedFlow && typeof PaymentMethodCardPresentFields === 'function') {
            _set(data, 'usingCardPresentDedicatedFlow', true);
            _set(data, 'paymentMethod.paymentMethodCategory', 'CP_POS');
            _set(data, 'paymentMethod.type', 'CP_POS');
            _set(data, 'paymentMethod.accountNumber', '9999999999999999');
        }
    }
    // post process when callback override changes, reset validation on callback number
    onCallbackOverrideChange = (name, value, data) => {
        const {state} = this.props,
            callbackNumberField = 'paymentMethod.callbackNumber',
            callbackNumber = _get(data, callbackNumberField);
        state.validationState[callbackNumberField] = state.validateField(callbackNumberField, callbackNumber);
    }
    // added post process for IVR lanuage selection and can be updated if required 
    onLanguageChange(name, value) {
        return;
    }

    // post process when category changes, reset fields and initialize type
    onPmCategoryChange(name, value, data) {
        const {state, lastUsedPm} = this.props,
            {config} = state,
            {paymentConfig} = config,
            {paymentMethods} = paymentConfig;

        if (isWalletPM(value)) {
            let wallet = this.findWallet(value);
            _set(data, 'paymentMethod.token', wallet.token);
            _set(data, 'paymentMethod.type', wallet.type);
            _set(data, 'paymentMethod.walletCategory',paymentMethods.find((pm)=>(pm.code===wallet.type)).categoryCode);
            _set(data, 'paymentMethod.accountNumber', wallet.accountNumber);
        } else {
            _set(data, 'paymentMethod.token', '');
            _set(data, 'paymentMethod.type', '');
        }

        const fields = FieldsConfig[value];
        if (fields) {
            for (let field of fields) {
                _set(data, field, '');
                state.resetError(field);
            }
            // If category has a single item, set the type now
            const pmList = paymentMethods.filter(pm => pm.categoryCode === value);
            const pmType = (pmList.length === 1 ? pmList[0].code : '');
            _set(data, 'paymentMethod.type', pmType);
            if (value === 'DD') {
                // for eCheck preselect the first type
                _set(data, 'paymentMethod.type', pmList[0].code);
            }
        }

        _unset(data, 'paymentMethod.callbackNumber');
        _unset(data, 'paymentMethod.callbackNumberExt');
        _unset(data, 'paymentMethod.doCallback');
        _set(data, 'paymentMethod.ivrLanguagePrefrenceShow',false);
            

        // if last used pm is selected, copy payment method details
        // into the payment method object, for the purpose of displaying
        // the details in the receipt
        if (value === "LAST_USED_PM") {
            this.copyPaymentMethod(lastUsedPm, data.paymentMethod);
        } else if (value === "CP_POS") {
            // For card-present TXNs, in which the payment header is processed 
            // in two stages, use a placeholder card number when initially
            // creating the header before the card has been read.
            _set(data, 'paymentMethod.accountNumber', '9999999999999999');
        } else if (value === 'CC_IVR' || value === 'DC_IVR' || value === 'DD_IVR' || value === 'PD_IVR') {
            if(config.paymentConfig.ivrLanguagePrefrenceEnabled){
                _set(data, 'paymentMethod.ivrLanguagePrefrenceShow',true);
            }else{
                _set(data, 'paymentMethod.ivrLanguagePrefrenceShow',false);
            }
            if (paymentConfig.ivrCallbackModeEnabled) {
                if (paymentConfig.ivrCallbackNrPrefix) {
                    _set(data, 'paymentMethod.callbackNumber', paymentConfig.ivrCallbackNrPrefix);
                } else if (paymentConfig.ivrCallbackModeBackToAgent) {
                    if (paymentConfig.permissionCallbackNumberFromUserProfile) {
                        _set(data, 'paymentMethod.callbackNumber', paymentConfig.agentDayPhoneNr);
                        _set(data, 'paymentMethod.callbackNumberExt', paymentConfig.agentDayPhoneExt);
        			}

                    _set(data, 'paymentMethod.doCallback', true);
                    if (!paymentConfig.ivrCallbackAgentDefault && paymentConfig.ivrCallbackAgentOverride) {
                        _set(data, 'paymentMethod.doCallback', false);
                    }
                } else if (paymentConfig.ivrCallbackModeQueue) {
                    _set(data, 'paymentMethod.callbackNumber', paymentConfig.ivrCallbackNr);
                }
            }
        }

        // trigger recalculation of the conv fee and totals
        // FIXME: Use better event handling
        for (let payment of data.payments) {
            payment.header.paymentAmount = payment.header.paymentAmount + 0;
        }
        if(!this.disableUpdateTotals) {
            updateTotals(data);
        }

        // revalidate email field because for certain billers with certain payment methods,
        // email is required, only revalidate if error exists (to fix scenario if email was
        // required for one payment method but not another)
        const emailField = 'customer.email',
            email = _get(data, emailField);
        if(state.validationState[emailField]){
            state.validationState[emailField] = state.validateField(emailField, email);
        }


    };

    /**
     * This method takes the selected wallet value from the dropdown,
     * such as: WALLET-0, WALLET-1, etc, and returns the
     * corresponding wallet object.
     *
     * @param selected
     */
    findWallet(selected) {
        const {state} = this.props,
            {data} = state,
            {currentUserWallet} = data;

        let index = getWalletIndex(selected);
        return currentUserWallet[index];
    }

    copyPaymentMethod(src, dest) {
        dest.type = src.type;
        dest.accountNumber = src.maskedAccountNumber;
        dest.creditCardExpiryDate = src.creditCardExpiryDate;
        dest.cardHolderName = src.cardHolderName;
        if (src.maskedRoutingNumber) {
            dest.routingNumber = src.maskedRoutingNumber;
        }
    }

    /**
     * Post process for CC/DC card number input. Determines the card type
     * based on the card number.
     * @param name
     * @param value
     * @param data
     */
    onCardNumberChanged(name, value, data) {
        const category = data.paymentMethod.paymentMethodCategory;
        const type = validators.identifyCardType(value, category);
        data.paymentMethod.type = type;
        // if user switched from Amex to something else, there could be 4 characters in the cvv field
        // since it's not amex anymore we will truncate cvv to first 3 characters
        let cvv = data.paymentMethod.cvv;
        if (type !== 'AMEX') {
            cvv = cvv ? cvv.substring(0, 3) : '';
        }
        data.paymentMethod.cvv = cvv;
    }

    render() {
        const {paymentMethod, active, lastUsedPm, showIcon, state} = this.props;
        let {showHeader} = this.props;
        if (typeof showHeader === 'undefined') {
            showHeader = true;
        }
        const {config, data} = state;
        const {currentUserWallet} = data;
        const {messages, paymentConfig, useCardPresentDedicatedFlow} = config;
        const {payWithPreviousPmSupported} = paymentConfig;
        const {
            PaymentMethodCCFields,
            PaymentMethodDCFields,
            PaymentMethodDDFields,
            PaymentMethodPDFields,
            PaymentMethodLastUsedFields,
            PaymentMethodCashFields,
            PaymentMethodCardPresentFields,
            PaymentMethodWalletFields,
            PaymentMethodCategory
        } = this.appComponents;
        const allowCardPresentManualEntry = paymentConfig.permissionCardPresentManualEntry ? !paymentConfig.cardPresentHideManualEntry : false;

        // FIXME: not the place to change state, but for now
        if (paymentConfig.paymentMethodCategories.length === 1 && !paymentMethod.paymentMethodCategory && !lastUsedPm) {
            paymentMethod.paymentMethodCategory = paymentConfig.paymentMethodCategories[0].code;
            let filteredPm = paymentConfig.paymentMethods.filter(pm => pm.categoryCode === paymentMethod.paymentMethodCategory);
            paymentMethod.type = filteredPm[0].code;
            for (let payment of data.payments) {
                payment.header.paymentAmount = payment.header.paymentAmount + 0;
            }
            if(!this.disableUpdateTotals) {
                updateTotals(data);
            }
        }

        let reactRows = [];
        switch (paymentMethod.paymentMethodCategory) {
            case"CC":
                reactRows.push(
                    <PaymentMethodCCFields
                        key="PaymentMethodCC"
                        config={config}
                        paymentMethod={paymentMethod}
                        onCardNumberChanged={this.onCardNumberChanged}
                        appComponents={this.appComponents}/>
                );
                if (payWithPreviousPmSupported) {
                    reactRows.push(
                        <SaveForReuseCheck
                            key="SaveForReuseCC"
                            config={config}/>
                    );
                }
                break;
            case"DC":
                reactRows.push(
                    <PaymentMethodDCFields
                        key="PaymentMethodDC"
                        config={config}
                        paymentMethod={paymentMethod}
                        onCardNumberChanged={this.onCardNumberChanged}
                        appComponents={this.appComponents}/>
                );
                if (payWithPreviousPmSupported) {
                    reactRows.push(
                        <SaveForReuseCheck
                            key="SaveForReuseDC"
                            config={config}/>
                    );
                }
                break;
            case"DD":
                reactRows.push(
                    <PaymentMethodDDFields
                        key="PaymentMethodDD"
                        paymentMethod={paymentMethod}
                        state={state}
                        appComponents={this.appComponents}/>
                );
                if (payWithPreviousPmSupported) {
                    reactRows.push(
                        <SaveForReuseCheck
                            key="SaveForReuseDD"
                            config={config}/>
                    );
                }
                break;
            case"PD":
                reactRows.push(
                    <PaymentMethodPDFields
                        key="PaymentMethodPD"
                        paymentMethod={paymentMethod}
                        state={state}
                        appComponents={this.appComponents}/>
                );
                if (payWithPreviousPmSupported) {
                    reactRows.push(
                        <SaveForReuseCheck
                            key="SaveForReusePD"
                            config={config}/>
                    );
                }
                break;
            case "LAST_USED_PM":
                reactRows.push(
                    <PaymentMethodLastUsedFields
                        key="PaymentMethodLastUsedPm"
                        config={config}
                        lastUsedPm={lastUsedPm}/>
                );
                break;
            case "CASH":
                reactRows.push(
                    <PaymentMethodCashFields
                        key="PaymentMethodCash"
                        config={config}
                        paymentMethod={paymentMethod}
                        state={state}
                        appComponents={this.appComponents}
                    />
                );
                break;
            case "CP_POS":
                reactRows.push(
                    <PaymentMethodCardPresentFields
                        key="PaymentMethodCardPresent"
                        config={config}
                        allowManualEntry={allowCardPresentManualEntry}
                        state={state}
                    />
                );
                break;
            case "CC_IVR":
            case "DC_IVR":
            case "DD_IVR":
            case "PD_IVR":
                reactRows.push(
                    <PaymentMethodIVRFields
                        key="PaymentMethodIVR"
                        paymentMethod={paymentMethod}
                        postProcess={this.onCallbackOverrideChange}
                        config={config}/>
                );
                break;
            case "CURRENT":
                //No fields will be populated in case of CURRENT option
                break;
        }

        // When a wallet method was selected
        if (isWalletPM(paymentMethod.paymentMethodCategory)) {
            const wallet = this.findWallet(paymentMethod.paymentMethodCategory);

            if (wallet) {
                reactRows.push(
                    <PaymentMethodWalletFields
                        key="PaymentMethodWallet"
                        config={config}
                        wallet={wallet}/>
                );
            }
        }

        return (
            <div className={classnames({"opp-section": true, "disable-section": !active})} id="PaymentMethod">
                <PaymentMethodHeader
                    showHeader={showHeader}
                    showIcon={showIcon}
                />    
                <div className="row">
                    <div className="col-12">
                        <div className="card card-v2">
                            <div className="card-body">
                                <div className="row mt-2">
                                    {useCardPresentDedicatedFlow ? (
                                        <PaymentMethodCardPresentFields
                                        key="PaymentMethodCardPresent"
                                        config={config}
                                        allowManualEntry={allowCardPresentManualEntry}
                                        state={state}
                                        />
                                    ) : (
                                        <React.Fragment>
                                    		<PaymentMethodCategory
                                        		key="PaymentMethodCategory"
                                        		config={config}
                                        		lastUsedPm={lastUsedPm}
                                        		currentUserWallet={currentUserWallet}
                                        		postProcess={this.onPmCategoryChange}
                                                state={state}
                                    		/>
                                            <IVRLanguagePreference
                                        		key="IVRLanguagePreference"
                                        		config={config}
                                        		lastUsedPm={lastUsedPm}
                                        		currentUserWallet={currentUserWallet}
                                        		postProcess={this.onLanguageChange}
                                                state={state}
                                    		/>
                                    		{reactRows}
                                        </React.Fragment>
                                        
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <React.Fragment>
                    <StaticAchAgreement
                        config={config}/>
                </React.Fragment>
            </div>
        );
    }
}

const PaymentMethodHeader = ({showHeader, showIcon}) => {

    return (
        <React.Fragment>
            {showHeader && (
                <h3 className="section-title">
                    {showIcon && (
                        <span className="section-icon">
                            <img src={messages["opp.icon-payment-method"] || "images/icon-payment-information.png"}
                                    alt="Payment Method Information"/>
                        </span>)
                    }
                    <span className="section-text">{messages["otp.paymentMethodInfo.title"]}</span>
                </h3>)
            }
        </React.Fragment>
    );
};


/**
 * @param config
 * @param lastUsedPm
 * @param currentUserWallet
 * @param postProcess
 */
const PaymentMethodCategory = ({config, lastUsedPm, currentUserWallet, postProcess, customFilter, compareFunction,state}) => {
    const {paymentConfig, messages, channelCode} = config;
    const lastUsedPmEnabled = (paymentConfig.payWithPreviousPmSupported && (channelCode !== "AD" || paymentConfig.payWithPreviousPmSupportedAd));
    const hasLastUsedPm = lastUsedPmEnabled && lastUsedPm;
    const emptyValue = messages["select.label"];
    // make a copy of the category list
    let pmCatList = paymentConfig.paymentMethodCategories.map(item => ({
        code: item.code, description: item.description
    }));

    if(typeof customFilter === 'function'){
        pmCatList = pmCatList.filter(customFilter);
    }

    if (hasLastUsedPm) {
        // Get localized name of payment method type
        // add last used pm to the start of the list
        pmCatList.unshift({
            code: "LAST_USED_PM",
            description: `${lastUsedPm.type} ${lastUsedPm.accountNumber} (${messages["opp.lastUsedPm.short.label"]})`
        });
    }

    // adds user profiles to the list of payment method category
    if (currentUserWallet && currentUserWallet.length > 0) {
        let walletList = currentUserWallet.map((item, idx) => {
            const pm = paymentConfig.paymentMethods.find(pm => (pm.code === item.type));
            let walletNickname = messages["opp.walletIndicator.label"];
            if (paymentConfig.walletNicknameEnabled && item.profileDescription) {
                walletNickname = `(${item.profileDescription})`;
            }
            return {
                code: `WALLET-${idx}`,
                description: `${pm.description} ${item.accountNumber} ${walletNickname}`
            }
        });
        walletList.forEach(item => pmCatList.unshift(item));
    }

    if(typeof compareFunction === 'function') {
        pmCatList.sort(compareFunction);
    }

    return (
        <React.Fragment>
            <div className="col-md-6 col-12">
                {pmCatList.length === 1 ?
                    <div className="row">
                        <div className="col-12">
                            <span className="display-label">{messages["payment.method.label"]}</span>
                            <span className="display-plaintext">{pmCatList[0].description}</span>
                        </div>
                    </div>
                    :
                    <SelectField
                        name="paymentMethod.paymentMethodCategory"
                        description={messages["payment.method.label"]}
                        selectList={pmCatList}
                        autoComplete="off"
                        postProcess={postProcess}
                        aria-required="true"
                        validations={{
                            required: "Please select a payment method"
                        }}>
                        <option key="Empty" value="">{emptyValue}</option>
                    </SelectField>
                }
            </div>

        </React.Fragment>
    );
};

/**
 * Shows IVR language selection dropdown if configs are enabled
 * and PaymentMethod category is DD_IVR ,DC_IVR, CC_IVR, PD_IVR
 * @param config
 * @param state
 * @param postProcess
 */
 const IVRLanguagePreference = ({config, postProcess, state}) => {
    const {paymentConfig, messages, channelCode} = config;
    const {data} = state;
    const emptyValue = messages["select.label"];
    const hasIvrLanguageEnabled = data.paymentMethod.ivrLanguagePrefrenceShow;
    const ivrLanguageList = paymentConfig.ivrLanguageList? paymentConfig.ivrLanguageList : [];
    let ivrLangList = ivrLanguageList.map(item => ({
        code: item.code, description: item.description
    }));
    return (
        <React.Fragment>
            {hasIvrLanguageEnabled && (
            <div className="col-md-6 col-12">
                {ivrLangList.length === 1 ?
                    <div className="row">
                        <div className="col-12">
                            <span className="display-label">{messages["languagePreference.label"]}</span>
                            <span className="display-plaintext">{ivrLangList[0].description}</span>
                        </div>
                    </div>
                    :
                    <SelectField
                        name="paymentMethod.ivrLanguagePreference"
                        description={messages["languagePreference.label"]}
                        selectList={ivrLangList}
                        postProcess={postProcess}
                        autoComplete="off"
                        aria-required="true"
                        validations={{
                            required: "Please select an IVR language"
                        }}>
                        <option key="Empty" value="">{emptyValue}</option>
                    </SelectField>
                }
            </div>)}
        </React.Fragment>
    );
};

/**
 * Layout for the Transfer to IVR fields. These fields are only for setting up the flow to transfer back to the agent.
 * @param config
 * @returns {*}
 * @constructor
 */
const PaymentMethodIVRFields = ({config, paymentMethod, postProcess}) => {
    const {messages, paymentConfig} = config,
        callbackOverrideAvailable = paymentConfig.ivrCallbackAgentOverride && paymentConfig.ivrCallbackModeBackToAgent;
    
    return ( (paymentConfig.ivrCallbackModeEnabled) ? 
        <React.Fragment>
                    <div key="callbackNumber" className="col-4 col-xs-12">
                        <TextField
                            name="paymentMethod.callbackNumber"
                            description={messages["callbackNumber.label"]}
                            className="md-form form-group"
                            placeholder={messages["callbackNumber.tip"]}
                            validations={{
                                required: (value, state) => ((!callbackOverrideAvailable || paymentMethod.doCallback) && !value) ? 'Callback number is required' : '',
                                regexp: {regexps: /^[0-9]{3}[ ]*[0-9]{3}[ ]*[0-9]{4}$/, msg: 'Callback number is not valid'}
                            }}
                            cleaveOptions={{phone: true, phoneRegionCode: 'US'}}
                        />
                    </div>
                    <div key="callbackNumberExt" className="col-2 col-xs-6">
                        <TextField
                            name="paymentMethod.callbackNumberExt"
                            description={messages["extension.label"]}
                            className="md-form form-group"
                            placeholder={messages["extension.label"]}
                            // extension is optional but must be numeric non-zero if not empty
                            validations={{regexp: {regexps: /^\d{0,10}$/, msg: 'Extension is not valid'}}}
                        />
                    </div>
            {callbackOverrideAvailable ? (
                <div key="doCallback" className="col-6 col-xs-6">
                    <CheckboxField
                        key="paymentMethod-doCallback"
                        name="paymentMethod.doCallback"
                        description={messages["callbackDecision.label"]}
                        postProcess={postProcess}
                        className="form-check pl-0"
                        inputClass="form-check-input"
                        aria-required="false"
                        labelClass="form-check-label"/>
                </div>) : ''}
        </React.Fragment>
        : ''
    );
};

/**
 *
 */
const SaveForReuseCheck = ({config}) => {
    const {messages} = config;

    if (config.appCode === 'cp-opp') {
        // Do not show this component in CP-OPP
        return null
    }

    return (
        <React.Fragment>
            <div className="col-12 form-group m-0 saveForReuse order-6">
                <CheckboxField
                    key="paymentMethod-saveForReuse"
                    name="paymentMethod.saveForReuse"
                    description={messages["payment.method.save.for.reuse"]}
                    className="form-check pl-0"
                    inputClass="form-check-input"
                    aria-required="false"
                    labelClass="form-check-label"/>
            </div>
        </React.Fragment>
    );
};


const BankAccountHolderName = ({messages}) => {
    return (
        <TextField
            name="paymentMethod.cardHolderName"
            description={messages["payment.account.holder.label"]}
            placeholder={messages["payment.account.holder.tip"]}
            maxLength="256"
            className="md-form form-group"
            labelClass="active"
            validations={{
                required: messages["pmBankAccountHolderName.empty"],
                name: messages["pmBankAccountHolderName.invalid"] || "Bank account holder name is invalid"
            }}
            autoComplete="off"/>
    );
};

const CheckAccountHolderName = ({messages}) => {
    return (
        <TextField
            name="paymentMethod.cardHolderName"
            description={messages["payment.account.holder.label"]}
            placeholder={messages["payment.account.holder.tip"]}
            maxLength="256"
            className="md-form form-group"
            labelClass="active"
            validations={{
                required: messages["pmBankAccountHolderName.empty"],
                name: messages["pmBankAccountHolderName.invalid"] || "Bank account holder name is invalid"
            }}
            autoComplete="off"/>
    );
};


export default PaymentMethodInfo;
export {
    PaymentMethodInfo,
    PaymentMethodIVRFields,
    BankAccountHolderName,
    CheckAccountHolderName,
    PaymentMethodCategory
};
